import axios from 'axios';
import config from '@/config';
import logger from '@/utils/logger';
import { parseApiResponse, handleApiError } from '@/utils/apiResponse';

/**
 * 认证API服务
 */
class AuthAPI {
  /**
   * 用户登录
   * @param {string} username - 登录名
   * @param {string} password - 明文密码
   * @returns {Promise} 返回登录结果，包含token、jti、exp和用户信息
   */
  async login(username, password) {
    try {
      const url = config.getApiUrl(config.apiEndpoints.auth.login);
      
      // 使用 POST 请求，请求体格式：{ username, password }
      const response = await axios.post(url, {
        username,
        password
      });
      
      // 添加调试日志
      logger.debug('登录API响应:', response.data);
      
      // 使用统一的响应解析函数处理 ApiResponse 格式
      const result = parseApiResponse(response);
      
      // 处理特殊错误码
      if (!result.success && result.code) {
        if (result.code === 401) {
          result.message = '用户名或密码错误';
        } else if (result.code === 403) {
          result.message = '账户已禁用，请联系管理员';
        }
      }
      
      return result;
    } catch (error) {
      // 使用统一的错误处理函数
      const errorResult = handleApiError(error);
      
      // 处理特殊错误码
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          // 检查是否是 ApiResponse 格式
          if (data && data.code === 401) {
            return {
              success: false,
              message: data.message || '用户名或密码错误',
              code: 401
            };
          }
          return {
            success: false,
            message: '用户名或密码错误',
            code: 401
          };
        } else if (status === 403) {
          // 检查是否是 ApiResponse 格式
          if (data && data.code === 403) {
            return {
              success: false,
              message: data.message || '账户已禁用，请联系管理员',
              code: 403
            };
          }
          return {
            success: false,
            message: '账户已禁用，请联系管理员',
            code: 403
          };
        }
      }
      
      return errorResult;
    }
  }

  /**
   * 获取欢迎配置信息
   * 自动从登录 Token 获取管理员ID（通过 Authorization: Bearer <token> 请求头）
   * @param {string} token - JWT令牌（可选，通常从请求头自动获取）
   * @param {string} adminId - 管理员ID（可选，通常不需要手动传入）
   * @returns {Promise} 返回欢迎配置信息列表
   */
  async getWelcomes(token = null, adminId = null) {
    try {
      const url = config.getApiUrl(config.apiEndpoints.auth.welcomes);
      
      const config = {
        headers: {}
      };
      
      // 如果提供了 token，添加到请求头
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // adminId 参数通常不需要，后端会从 token 中获取
      const params = {};
      if (adminId) params.adminId = adminId;
      
      const response = await axios.get(url, { ...config, params });
      
      // 使用统一的响应解析函数处理 ApiResponse 格式
      const result = parseApiResponse(response);
      
      // 确保返回数组格式
      if (result.success && result.data) {
        return {
          success: true,
          data: Array.isArray(result.data) ? result.data : []
        };
      }
      
      return {
        success: false,
        message: result.message || '获取欢迎配置失败',
        data: []
      };
    } catch (error) {
      const errorResult = handleApiError(error);
      return {
        success: false,
        message: errorResult.message || '获取欢迎配置失败',
        data: []
      };
    }
  }
}

// 创建单例实例
const authAPI = new AuthAPI();

export default authAPI;

